import React from 'react';
import { Box, Button, Container, Dialog, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { FaxStatusLabel } from '../../components/labels/StatusLabels';
import FaxStatusStep from '../../sections/main/user/profile/admin-client/fax-center/FaxStatusStep';
import FaxSearch from '../../../src/sections/main/fax-lists/FaxSearch';
import useSettings from 'src/hooks/useSettings';
import { useFaxManagement, FaxListAction } from '../../../src/hooks/useFaxManagement';
import DatagridToolbar from '../DataGridToolbar';

const DEFAULT_PAGE_SIZE = 50;

const FaxManagement: React.FC = () => {
    const { themeStretch } = useSettings();

    // Use custom hook
    const {
        superQuery,
        handleSearch,
        listedFaxJobs,
        faxJobsLoading,
        faxJobsError,
        paginationModel,
        handlePaginationChange,
        sortModel,
        handleSortModelChange,
        handleCellClick,
        handleDownloadFax,
        downloadLoading,
        action,
        viewFaxDetails,
        closeModal,
    } = useFaxManagement(DEFAULT_PAGE_SIZE);

    // Column definitions
    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: 'faxJobId',
                headerName: 'ID',
                width: 200,
                valueFormatter: (params) => params.value.substring(0, 20) + '...',
            },
            {
                field: 'status',
                headerName: 'Status',
                width: 200,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => <FaxStatusLabel status={params.value} />,
            },
            {
                field: 'createdAt',
                headerName: 'Created',
                width: 200,
                valueFormatter: (params) => format(new Date(params.value), 'MMM d, yyyy h:mm a'),
            },
            {
                field: 'recipientFax',
                headerName: 'Recipient',
                width: 250,
                valueFormatter: (params) => {
                    const num = params.value.replace(/[^\d]/g, '');
                    if (num.length === 10) {
                        return `(${num.slice(0, 3)}) ${num.slice(3, 6)}-${num.slice(6)}`;
                    }
                    return params.value;
                },
            },
            {
                field: 'requestedBy',
                headerName: 'Requested By',
                width: 200,
                valueGetter: (params) =>
                    params.value
                        ? `${params.value.firstName || ''} ${params.value.lastName || ''}`.trim() ||
                          params.value.name
                        : `ID: ${params.row.requestedById}`,
            },
            {
                field: 'documentSnapshot',
                headerName: 'Documents',
                width: 200,
                valueFormatter: (params) => `${params.value?.length || 0} docs`,
            },
            {
                field: 'actions',
                headerName: 'Actions',
                width: 200,
                sortable: false,
                renderCell: (params) => (
                    <Stack direction="row" spacing={1}>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => viewFaxDetails(params.row)}
                        >
                            View
                        </Button>
                        {params.row.status === 'COMPLETED' && (
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() => handleDownloadFax(params.row.faxJobId)}
                                disabled={downloadLoading}
                            >
                                Download
                            </Button>
                        )}
                    </Stack>
                ),
            },
        ],
        [downloadLoading, viewFaxDetails, handleDownloadFax]
    );

    // Prepare data for DataGrid
    const gridRows = useMemo(
        () =>
            listedFaxJobs?.faxJobs?.map((fax: any) => ({
                id: fax.faxJobId,
                ...fax,
            })) || [],
        [listedFaxJobs]
    );

    return (
        <Container maxWidth={themeStretch ? false : 'xl'}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h4" sx={{ mb: 4 }}>
                    Fax Management
                </Typography>

                <FaxSearch
                    initialQuery={superQuery || {}}
                    onSearch={handleSearch}
                    loading={faxJobsLoading}
                />

                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                    <DataGrid
                        getRowId={(row) => row?.id}
                        rows={gridRows}
                        columns={columns}
                        pagination={true}
                        paginationMode="server"
                        paginationModel={paginationModel}
                        onPaginationModelChange={handlePaginationChange}
                        pageSizeOptions={[DEFAULT_PAGE_SIZE, 100]}
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        loading={faxJobsLoading || !listedFaxJobs}
                        rowCount={listedFaxJobs?.totalCount || 0}
                        sx={{ flexGrow: 1 }}
                        slots={{
                            toolbar: DatagridToolbar,
                        }}
                        localeText={{
                            noRowsLabel: faxJobsError?.message || 'No fax jobs found',
                        }}
                        onCellClick={handleCellClick}
                        autoHeight
                    />
                </Box>

                {/* Fax Details Modal */}
                {action && action.type === FaxListAction.VIEW_DETAILS && (
                    <Dialog open={true} onClose={closeModal} maxWidth="lg" fullWidth>
                        <FaxStatusStep faxRecord={action.faxJob} onClose={closeModal} />
                    </Dialog>
                )}
            </Box>
        </Container>
    );
};

export default FaxManagement;
